<template>
  <v-container id="device" fluid>
    <v-card v-if="device">
      <v-system-bar color="device" />
      <v-skeleton-loader
        :loading="loading"
        type="actions"
        transition="fade-transition"
      >
        <v-toolbar flat>
          <v-icon left large color="device">mdi-camera-control</v-icon>
          <v-toolbar-title>{{ getDeviceName(device) }}</v-toolbar-title>
          <v-spacer />
          <v-btn :loading="downloading" :disabled="true" color="device">
            <v-icon left>mdi-light mdi-download</v-icon>
            再生データダウンロード
          </v-btn>
        </v-toolbar>
      </v-skeleton-loader>

      <v-dialog
        persistent
        max-width="600px"
        v-model="deleteDialog"
        @keydown.esc="deleteDialog = false"
      >
        <confirm-delete-card
          subtitle="ログレコード"
          @cancel="deleteDialog = false"
          @confirm="deleteLogRecords"
        />
      </v-dialog>

      <v-card-text>
        <v-row>
          <v-col cols="2"
            ><v-icon left large>mdi-identifier</v-icon>
            識別子
          </v-col>
          <v-col cols="10">
            <v-chip
              label
              v-clipboard="device.id"
              v-clipboard:success="showCopySuccessSnack"
              v-clipboard:error="showCopyErrorSnack"
              @click="() => {}"
            >
              <v-icon small left>mdi-content-copy</v-icon>
              {{ getDeviceId(device) }}
            </v-chip>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="2"
            ><v-icon left large>mdi-head-outline</v-icon>
            認識回数
          </v-col>
          <v-col cols="10">
            <v-chip label>
              {{ getEventCount(device) }}
            </v-chip>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="2"
            ><v-icon left large>mdi-play-circle-outline</v-icon>
            再生回数
          </v-col>
          <v-col cols="10">
            <v-chip label>
              {{ getPlayCount(device) }}
            </v-chip>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="2"
            ><v-icon left large>mdi-exclamation</v-icon>
            エラー数
          </v-col>
          <v-col cols="10">
            <v-chip label>
              {{ getErrorCount(device) }}
            </v-chip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card>
      <v-system-bar color="log" />
      <v-toolbar flat>
        <v-icon large left color="log">mdi-view-list-outline</v-icon>
        <v-toolbar-title>
          ログ
        </v-toolbar-title>
        <v-spacer />
        <v-text-field
          v-model="logSearch"
          prepend-icon="mdi-magnify"
          label="検索"
          single-line
          hide-details
          outlined
          dense
          clearable
        />
        <v-spacer />
        <v-btn
          color="error"
          @click="deleteDialog = true"
          :disabled="logs.length === 0"
        >
          <v-icon left>mdi-light mdi-delete</v-icon>
          すべてを削除
        </v-btn>
      </v-toolbar>
      <v-card-title>
        <v-chip-group multiple v-model="logFilters">
          <v-chip
            dark
            filter
            outlined
            v-for="level in logLevels"
            :key="level.value"
            :color="level.color"
          >
            <v-icon left>{{ level.icon }}</v-icon>
            {{ level.text }}
          </v-chip>
        </v-chip-group>
      </v-card-title>
      <v-data-table
        :headers="logHeaders"
        :items="logs"
        :loading="loading"
        :sort-by.sync="logSortBy"
        :sort-desc.sync="logSortDesc"
        :search="logSearch"
        no-data-text="データがありません"
        loading-text="ロード中"
        class="pa-4"
      >
        <template v-slot:item.level="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                large
                v-on="on"
                :color="getLogRecordLevelField(item, 'color')"
              >
                {{ getLogRecordLevelField(item, 'icon') }}
              </v-icon>
            </template>
            <span>
              {{ getLogRecordLevelField(item) }}
            </span>
          </v-tooltip>
        </template>
        <template v-slot:item.timestamp="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                {{ getLogRecordTimestamp(item) }}
              </span>
            </template>
            <span>{{ getLogRecordTimestampFull(item) }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.origin="{ item }">
          {{ getLogRecordOriginField(item) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-chip
            label
            dark
            color="log"
            v-clipboard="item"
            v-clipboard:success="showCopySuccessSnack"
            v-clipboard:error="showCopyErrorSnack"
            @click="() => {}"
          >
            <v-icon small left>mdi-content-copy</v-icon>
            コピー
          </v-chip>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { fmt, fmtToNow } from '@/plugins/datefns'
import { deviceFunctionsMixin } from '@/plugins/mixins'
import ConfirmDeleteCard from '@/components/dialogs/ConfirmDeleteCard'
import {
  getDeviceLogs,
  getDeviceRef,
  createBatch
} from '@/firebaseAccess/firestoreAccess'

export default {
  name: 'Device',
  components: {
    ConfirmDeleteCard
  },
  mixins: [deviceFunctionsMixin],
  data: () => ({
    loading: true,
    downloading: false,
    deleteDialog: false,
    logSortBy: ['timestamp'],
    logSortDesc: [true],
    logFilters: [],
    logSearch: ''
  }),
  computed: {
    ...mapGetters({
      device: 'devices/getDevice',
      logs: 'devices/logs/getLogs'
    }),
    logLevels() {
      return this.getDeviceLogRecordLevel()
    },
    logHeaders() {
      return [
        {
          text: '重要度',
          value: 'level',
          width: 100,
          filter: value => {
            if (this.logFilters.length === 0) return true
            const level = this.getDeviceLogRecordLevel().find(
              l => l.value === value
            )
            if (level) {
              return this.logFilters.includes(level.filter)
            }
            return false
          }
        },
        { text: '時間', value: 'timestamp', width: 180 },
        { text: '原因', value: 'origin', width: 100 },
        { text: 'メッセージ', value: 'message' },
        {
          text: '',
          value: 'actions',
          align: 'end',
          sortable: false,
          filterable: false
        }
      ]
    }
  },
  methods: {
    ...mapActions({
      showSnack: 'snackbar/showSnack',

      bindDeviceDocRef: 'devices/bindDocRef',
      bindDeviceLogsRef: 'devices/logs/bindRef'
    }),
    showErrorSnack(message) {
      this.showSnack({
        message: `失敗しました: ${message}`,
        color: 'error'
      })
    },
    getDeviceId(device) {
      if (device) {
        return device.id
      }
      return '--'
    },
    getDeviceName(device) {
      if (device) {
        return device.name
      }
      return '--'
    },
    getEventCount(device) {
      if (device.stats) {
        return device.stats.eventCount ?? 0
      }
      return 0
    },
    getPlayCount(device) {
      if (device.stats) {
        return device.stats.playbackCount ?? 0
      }
      return 0
    },
    getErrorCount(device) {
      if (device.stats) {
        return device.stats.errorCount ?? 0
      }
      return '--'
    },
    getLogRecordOriginField(log, field = 'text') {
      if (log !== undefined && log.origin !== undefined) {
        const origin = this.getDeviceLogRecordOrigin().find(
          s => s.value === log.origin
        )
        if (origin) {
          return origin[field]
        }
      }
      return '--'
    },
    getLogRecordLevelField(log, field = 'text') {
      if (log !== undefined && log.level !== undefined) {
        const level = this.getDeviceLogRecordLevel().find(
          s => s.value === log.level
        )
        if (level) {
          return level[field]
        }
      }
      return '--'
    },
    getLogRecordTimestamp(log) {
      if (log.timestamp !== undefined) {
        return fmtToNow(log.timestamp.toDate())
      }
      return '--'
    },
    getLogRecordTimestampFull(log) {
      if (log.timestamp !== undefined) {
        return fmt(log.timestamp.toDate())
      }
      return '--'
    },
    async deleteAllDeviceLogs(deviceId) {
      const records = await getDeviceLogs(deviceId)

      if (records.size > 0) {
        const deviceRef = getDeviceRef(deviceId)
        // Task to delete all logs.
        const batch = createBatch()
        records.forEach(doc => {
          batch.delete(doc.ref)
        })
        // Task to initilize error count.
        batch.set(deviceRef, { stats: { errorCount: 0 } }, { merge: true })
        // Commit delete and set action.
        return batch.commit()
      }

      return new Promise(() => {})
    },
    deleteLogRecords() {
      this.deleteDialog = false

      this.loading = true
      this.deleteAllDeviceLogs(this.device.id)
        .then(() => {
          this.showSnack({
            message: 'ログを削除しました',
            color: 'success'
          })
        })
        .catch(error => {
          console.log(error)
          this.showSnack({
            message: `ログの削除に失敗しました: ${error}`,
            color: 'error'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    showCopySuccessSnack() {
      this.showSnack({
        message: 'コピーしました',
        color: 'success'
      })
    },
    showCopyErrorSnack() {
      this.showSnack({
        message: 'コピーに失敗しました',
        color: 'error'
      })
    }
  },
  mounted() {
    this.loading = true

    const deviceId = this.$route.params.id

    const device = this.bindDeviceDocRef(deviceId)
    const logs = this.bindDeviceLogsRef(deviceId)

    Promise.all([device, logs])
      .catch(error => {
        this.showErrorSnack(error)
      })
      .finally(() => {
        this.loading = false
      })
  }
}
</script>

<style scoped></style>
